// Utility functions for formatting

/**
 * Format currency amount
 * @param amount Amount in cents (e.g., 50000 for €500.00) o en EUR si amountInCents es false (e.g. 500)
 * @param currency Currency code (e.g., 'eur', 'usd')
 * @param amountInCents If true (default), amount is in cents; if false, amount is in major units (EUR)
 */
export function formatCurrency(amount: number, currency: string = 'eur', amountInCents: boolean = true): string {
  const amountInUnits = amountInCents ? amount / 100 : amount;

  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountInUnits);
}

/** Placeholder mostrado cuando una fecha es nula, ausente o inválida. */
const INVALID_DATE_PLACEHOLDER = '—';

/**
 * Convierte la entrada a un Date válido, o `null` si no se puede.
 * Evita que `Intl.DateTimeFormat().format()` lance `RangeError: Invalid time value`
 * al recibir un `Date` inválido (backend devuelve null / string malformado).
 */
function toValidDate(date: string | Date | null | undefined): Date | null {
  if (date === null || date === undefined || date === '') return null;
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return Number.isNaN(dateObj.getTime()) ? null : dateObj;
}

/**
 * Format date to Spanish locale
 */
export function formatDate(date: string | Date | null | undefined): string {
  const dateObj = toValidDate(date);
  if (!dateObj) return INVALID_DATE_PLACEHOLDER;

  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(dateObj);
}

/**
 * Format date to Spanish locale using only the calendar day (no timezone shift).
 * Use when the backend sends UTC and you want to show the intended calendar date
 * (e.g. "2026-03-02T23:00:00Z" → 02/03/2026, not 03/03/2026).
 */
export function formatDateOnly(date: string | Date | null | undefined): string {
  if (date === null || date === undefined || date === '') {
    return INVALID_DATE_PLACEHOLDER;
  }
  if (typeof date === 'string') {
    const match = date.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) {
      const [, y, m, d] = match;
      return new Intl.DateTimeFormat('es-ES', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      }).format(new Date(Number(y), Number(m) - 1, Number(d)));
    }
  }
  const dateObj = toValidDate(date);
  if (!dateObj) return INVALID_DATE_PLACEHOLDER;
  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(dateObj);
}

/**
 * Format datetime to Spanish locale
 */
export function formatDateTime(date: string | Date | null | undefined): string {
  const dateObj = toValidDate(date);
  if (!dateObj) return INVALID_DATE_PLACEHOLDER;

  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(dateObj);
}

/**
 * Get user's IP address (approximation using public API)
 */
export async function getUserIP(): Promise<string> {
  try {
    const response = await fetch('https://api.ipify.org?format=json');
    const data = await response.json();
    return data.ip;
  } catch {
    return 'unknown';
  }
}

/**
 * Get user agent string
 */
export function getUserAgent(): string {
  return navigator.userAgent;
}

/**
 * Truncate text with ellipsis
 */
export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength) + '...';
}

/**
 * Formatea un UserBrief para mostrar en UI (creator, updater, agent, assignee).
 * Prioridad: name → email → "Usuario" si solo hay id.
 */
export function formatUserBrief(
  user: { id: string; name?: string | null; email?: string | null } | undefined | null
): string {
  if (!user) return 'Usuario desconocido';
  const name = user.name?.trim();
  if (name && name.length > 0) return name;
  const email = user.email?.trim();
  if (email && email.length > 0) return email;
  return 'Usuario';
}

